/**
 * Luna Intelligence client store.
 *
 * Drives the `<LunaPanel />` chat UI. Holds:
 *   - conversation turns (user/assistant with inline tool activity)
 *   - proposals waiting for user approval
 *   - credits balance
 *
 * Separate from `useEditor` so the Luna panel can mount/unmount freely
 * without disturbing the visual editor's state. Proposals carry both
 * old + new content so the diff view is purely client-side — no second
 * round trip when the user opens the diff.
 */

import { create } from 'zustand';
import {
  applyLunaProposal,
  fetchLunaBalance,
  listLunaPackages,
  purchaseLunaPackage,
  streamLunaChat,
  type EditorSession,
  type LunaBalance,
  type LunaCheckout,
  type LunaContextPayload,
  type LunaEvent,
  type LunaPackage,
} from '../api/client';
import { useEditor } from './store';

export interface ToolTrace {
  id: string;
  name: string;
  input: any;
  summary?: string; // populated when tool_result arrives
}

export interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  tools: ToolTrace[];
  streaming?: boolean; // true while assistant is still writing
  error?: string;
}

export type ProposalStatus = 'pending' | 'applied' | 'rejected' | 'applying' | 'error';

export interface Proposal {
  proposalId: string;
  turnId: string; // which assistant turn surfaced it
  kind: 'write' | 'delete';
  path: string;
  newContent?: string;
  oldContent?: string | null;
  reason?: string;
  status: ProposalStatus;
  errorMessage?: string;
}

interface LunaState {
  session: EditorSession | null;
  turns: ChatTurn[];
  proposals: Proposal[];
  /** Credits. null until balance has been fetched for the session. */
  balance: LunaBalance | null;
  /** Is a chat turn currently streaming? */
  sending: boolean;
  /** Abort controller for the current stream, if any. */
  _abort: AbortController | null;
  /** Panel open/closed (persisted). */
  open: boolean;

  /** Billing catalog + purchase UI. */
  packages: LunaPackage[];
  packagesLoading: boolean;
  buyOpen: boolean;
  /** Active checkout (PIX QR etc.) — set while the user pays. */
  checkout: LunaCheckout | null;
  purchaseLoading: boolean;
  purchaseError: string | null;

  init(session: EditorSession): Promise<void>;
  refreshBalance(): Promise<void>;
  setOpen(open: boolean): void;

  send(userMessage: string, ctx: LunaContextPayload): Promise<void>;
  cancel(): void;
  clear(): void;

  applyProposal(proposalId: string): Promise<void>;
  rejectProposal(proposalId: string): void;

  openBuyCredits(): Promise<void>;
  closeBuyCredits(): void;
  startPurchase(packageSlug: string, billingType: 'PIX' | 'CREDIT_CARD' | 'BOLETO'): Promise<void>;
  clearCheckout(): void;
}

function persistOpen(value: boolean) {
  try {
    localStorage.setItem('lc-luna-open', value ? '1' : '0');
  } catch {}
}

function readOpen(): boolean {
  try {
    const raw = localStorage.getItem('lc-luna-open');
    return raw === '1';
  } catch {
    return false;
  }
}

export const useLuna = create<LunaState>((set, get) => ({
  session: null,
  turns: [],
  proposals: [],
  balance: null,
  sending: false,
  _abort: null,
  open: readOpen(),

  packages: [],
  packagesLoading: false,
  buyOpen: false,
  checkout: null,
  purchaseLoading: false,
  purchaseError: null,

  async init(session) {
    const prev = get().session;
    // Don't wipe the conversation when React re-runs init after a
    // hot-reload for the same session.
    if (prev && prev.tenantId === session.tenantId && prev.themeId === session.themeId) {
      set({ session });
      return;
    }
    set({ session, turns: [], proposals: [], balance: null });
    await get().refreshBalance();
  },

  async refreshBalance() {
    const { session } = get();
    if (!session) return;
    try {
      const balance = await fetchLunaBalance(session);
      set({ balance });
    } catch (err) {
      console.warn('[luna] balance fetch failed', err);
    }
  },

  setOpen(open) {
    set({ open });
    persistOpen(open);
  },

  async send(userMessage, ctx) {
    const { session, turns, sending } = get();
    if (!session || sending) return;
    const trimmed = userMessage.trim();
    if (!trimmed) return;

    const userTurn: ChatTurn = {
      id: `u_${Date.now().toString(36)}`,
      role: 'user',
      text: trimmed,
      tools: [],
    };
    const assistantTurn: ChatTurn = {
      id: `a_${Date.now().toString(36)}`,
      role: 'assistant',
      text: '',
      tools: [],
      streaming: true,
    };
    set({
      turns: [...turns, userTurn, assistantTurn],
      sending: true,
    });

    /*
      Build the flat history the engine expects — só texto user/assistant,
      sem tool activity (o modelo reconstrói contexto pelas próprias
      respostas no loop do servidor).

      ATENÇÃO: turns de assistant que SÓ fizeram tool calls (propose_write
      sem texto narrativo) têm `text: ""`. A Anthropic API REJEITA
      mensagens com content vazio:
        `messages.N.content: text content blocks must contain non-empty text`

      Bug reportado: "depois de 3 coisas alteradas a IA quebra, nao
      consigo alterar mais nada". Causa: cada turn de proposal-sem-texto
      ia pra história como `{role: 'assistant', content: ''}`. Após
      acumular ~3, a próxima request batia 400 e a Luna ficava travada.

      Fix: substituir text vazio por placeholder descritivo. Mantém o
      fluxo da conversa (modelo sabe que houve uma turn) sem violar a
      regra de content não-vazio.

      Pra turns de assistant que TÊM proposals mas sem texto, o
      placeholder reflete isso explicitamente — modelo entende que já
      respondeu à pergunta anterior via mudança de arquivo.
    */
    const history = [...turns, userTurn].map((t) => {
      const text = t.text.trim();
      if (text) return { role: t.role, content: text };
      // Empty content — substitui por placeholder
      if (t.role === 'assistant') {
        const proposalsInTurn = get().proposals.filter((p) => p.turnId === t.id);
        if (proposalsInTurn.length > 0) {
          const paths = proposalsInTurn.map((p) => p.path).join(', ');
          return {
            role: 'assistant' as const,
            content: `(Proposed file change(s): ${paths})`,
          };
        }
        return { role: 'assistant' as const, content: '(No response.)' };
      }
      // user vazio NÃO deveria acontecer (send() já valida) — fallback defensivo
      return { role: t.role, content: '(empty)' };
    });

    const abort = new AbortController();
    set({ _abort: abort });

    try {
      await streamLunaChat(
        session,
        { messages: history, context: ctx, signal: abort.signal },
        (ev) => handleEvent(ev, assistantTurn.id, set, get)
      );
    } catch (err: any) {
      set((s) => ({
        turns: s.turns.map((t) =>
          t.id === assistantTurn.id
            ? { ...t, streaming: false, error: err?.message || 'Falha ao falar com Luna' }
            : t
        ),
      }));
    } finally {
      set((s) => ({
        sending: false,
        _abort: null,
        turns: s.turns.map((t) =>
          t.id === assistantTurn.id ? { ...t, streaming: false } : t
        ),
      }));
      void get().refreshBalance();
    }
  },

  cancel() {
    const { _abort } = get();
    if (_abort) _abort.abort();
  },

  clear() {
    set({ turns: [], proposals: [] });
  },

  async applyProposal(proposalId) {
    const { session, proposals } = get();
    const p = proposals.find((x) => x.proposalId === proposalId);
    if (!p || !session) return;
    if (p.status === 'applied' || p.status === 'applying') return;
    set({
      proposals: proposals.map((x) =>
        x.proposalId === proposalId ? { ...x, status: 'applying' } : x
      ),
    });
    try {
      console.log('[luna-apply] POST /luna/apply', { path: p.path, kind: p.kind });
      await applyLunaProposal(session, {
        path: p.path,
        content: p.newContent ?? '',
        kind: p.kind,
      });
      console.log('[luna-apply] write OK, marking proposal applied');
      set((s) => ({
        proposals: s.proposals.map((x) =>
          x.proposalId === proposalId ? { ...x, status: 'applied' } : x
        ),
      }));

      /*
        Sincroniza o code panel se o arquivo modificado está aberto numa
        tab. Sem isso, o buffer da tab fica stale (mostra conteúdo
        ANTERIOR à edição da AI), e se o user "salvar" no code panel
        depois, sobrescreve as mudanças da AI com o buffer velho.

        `refreshBufferIfOpen` é defensivo:
          - Tab limpa (sem edits do user) → atualiza buffer pro novo conteúdo
          - Tab com edits → mantém buffer, marca `externallyModified: true`
            (UI pode mostrar aviso de conflito)

        Import lazy do useCode pra evitar circular (luna-store ↔ code-store).
      */
      try {
        const { useCode } = await import('./code-store');
        if (p.path) {
          await useCode.getState().refreshBufferIfOpen(p.path);
        }
      } catch (codeErr) {
        console.warn('[luna-apply] code panel refresh failed', codeErr);
      }
      /*
        Luna gravou direto no storage. O editor frontend tem estado
        em memória (sections, headerGroup, footerGroup, etc.) que
        agora está stale. `reloadPageFromServer` busca a página do
        engine SEM enviar drafts — adota a versão recém-escrita como
        fonte da verdade e força o iframe a re-renderizar.

        Sem isso, o usuário precisava recarregar a página inteira
        (F5) pra ver as mudanças que aplicou via Luna. Bug reportado:
        "rolou um apply ... mas nao mudou nada no iframe ate eu
        recarregar".

        Pequeno delay antes do reload pra dar tempo das invalidações
        de cache cross-instância (Redis pub/sub) propagarem. Em deploy
        single-instance é desnecessário; em multi-instance o reload
        pode cair numa réplica que ainda não recebeu o pub/sub e
        retornar conteúdo stale do file cache local.
      */
      try {
        await new Promise((resolve) => setTimeout(resolve, 150));
        console.log('[luna-apply] calling reloadPageFromServer');
        const beforeHtml = useEditor.getState().initialHtml;
        await useEditor.getState().reloadPageFromServer();
        const afterHtml = useEditor.getState().initialHtml;
        console.log(
          '[luna-apply] reload done; html changed?',
          beforeHtml !== afterHtml,
          `(before: ${beforeHtml?.length || 0}b → after: ${afterHtml?.length || 0}b)`
        );
        if (beforeHtml === afterHtml) {
          console.warn(
            '[luna-apply] HTML did not change — engine may have served stale cache. Forcing iframe reload via initialHtml toggle.'
          );
          // Força o useEffect que observa initialHtml a re-disparar
          // setando empty + putting back. Truque comum quando o
          // value referencialmente é o mesmo mas precisamos re-render.
          useEditor.setState({ initialHtml: '' });
          await new Promise((resolve) => setTimeout(resolve, 0));
          useEditor.setState({ initialHtml: afterHtml });
        }
      } catch (reloadErr: any) {
        console.warn('[luna] reload after apply failed', reloadErr);
      }
    } catch (err: any) {
      set((s) => ({
        proposals: s.proposals.map((x) =>
          x.proposalId === proposalId
            ? { ...x, status: 'error', errorMessage: err?.message || 'Falha ao aplicar' }
            : x
        ),
      }));
    }
  },

  rejectProposal(proposalId) {
    set((s) => ({
      proposals: s.proposals.map((x) =>
        x.proposalId === proposalId ? { ...x, status: 'rejected' } : x
      ),
    }));
  },

  async openBuyCredits() {
    set({ buyOpen: true, purchaseError: null });
    const { session, packages } = get();
    if (!session || packages.length > 0) return;
    set({ packagesLoading: true });
    try {
      const pkgs = await listLunaPackages(session);
      set({ packages: pkgs });
    } catch (err: any) {
      set({ purchaseError: err?.message || 'Falha ao carregar pacotes' });
    } finally {
      set({ packagesLoading: false });
    }
  },

  closeBuyCredits() {
    set({ buyOpen: false, purchaseError: null });
  },

  async startPurchase(packageSlug, billingType) {
    const { session } = get();
    if (!session) return;
    set({ purchaseLoading: true, purchaseError: null, checkout: null });
    try {
      const checkout = await purchaseLunaPackage(session, { packageSlug, billingType });
      set({ checkout, purchaseLoading: false });
    } catch (err: any) {
      set({
        purchaseError: err?.message || 'Falha ao iniciar compra',
        purchaseLoading: false,
      });
    }
  },

  clearCheckout() {
    set({ checkout: null, purchaseError: null });
    void get().refreshBalance();
  },
}));

// ---------------------------------------------------------------------------
// SSE event → state mapper
// ---------------------------------------------------------------------------

function handleEvent(
  ev: LunaEvent,
  assistantTurnId: string,
  set: (partial: Partial<LunaState> | ((s: LunaState) => Partial<LunaState>)) => void,
  get: () => LunaState
) {
  switch (ev.type) {
    case 'text': {
      set((s: LunaState) => ({
        turns: s.turns.map((t) =>
          t.id === assistantTurnId ? { ...t, text: t.text + ev.delta } : t
        ),
      }));
      return;
    }
    case 'tool_use': {
      set((s: LunaState) => ({
        turns: s.turns.map((t) =>
          t.id === assistantTurnId
            ? {
                ...t,
                tools: [
                  ...t.tools,
                  { id: ev.id, name: ev.name, input: ev.input },
                ],
              }
            : t
        ),
      }));
      return;
    }
    case 'tool_result': {
      set((s: LunaState) => ({
        turns: s.turns.map((t) =>
          t.id === assistantTurnId
            ? {
                ...t,
                tools: t.tools.map((x) =>
                  x.id === ev.id ? { ...x, summary: ev.summary } : x
                ),
              }
            : t
        ),
      }));
      return;
    }
    case 'proposal': {
      const p = ev.proposal;
      set((s: LunaState) => {
        if (s.proposals.some((x) => x.proposalId === p.proposalId)) return s;
        return {
          proposals: [
            ...s.proposals,
            {
              proposalId: p.proposalId,
              turnId: assistantTurnId,
              kind: p.kind,
              path: p.path,
              newContent: p.newContent,
              oldContent: p.oldContent,
              reason: p.reason,
              status: 'pending' as ProposalStatus,
            },
          ],
        };
      });
      return;
    }
    case 'done': {
      // Balance is refreshed on turn completion by send(); the credits
      // info on this event is informational, but we can optimistically
      // update so the UI reflects the spend immediately.
      const credits = ev.credits;
      if (credits) {
        /*
          Se a cobrança falhou no backend, NÃO atualiza balance com
          balanceAfter (-1 ou stale) — deixa o `refreshBalance` no finally
          pegar o valor real. Adiciona erro visível na turn pra user saber
          que algo deu ruim no billing.

          Sem essa branch, o user via balance ir pra -1 (debit falhou) sem
          entender o motivo, OU não via balance descer nada e achava que
          tava de graça.
        */
        if (credits.chargeFailed) {
          set((s: LunaState) => ({
            turns: s.turns.map((t) =>
              t.id === assistantTurnId
                ? {
                    ...t,
                    error:
                      credits.chargeError ||
                      'Falha ao registrar uso (cobrança não realizada). Avise o suporte.',
                  }
                : t
            ),
          }));
        } else if (typeof credits.balanceAfter === 'number' && credits.balanceAfter >= 0) {
          // Cobrança OK — atualiza balance otimisticamente
          set((s: LunaState) => ({
            balance: s.balance
              ? { ...s.balance, balance: credits.balanceAfter }
              : s.balance,
          }));
        }
      }
      return;
    }
    case 'error': {
      set((s: LunaState) => ({
        turns: s.turns.map((t) =>
          t.id === assistantTurnId ? { ...t, error: ev.message, streaming: false } : t
        ),
      }));
      return;
    }
  }
}
